import { Button, ConfigProvider, Table, Tooltip, Popover } from "antd";
import { EyeFilled, CaretDownOutlined, CaretUpOutlined, MenuOutlined } from "@ant-design/icons";
import React, { useEffect, useState, useMemo, useCallback, createContext, useContext } from "react";
import { ViewHistoryComments } from "../modals/ViewHistoryComments";
import TableEmptyContent from "../TableEmptyContent/TableEmptyContent";
import { columnsArr } from "./HistoryTableUtils";
import { isSortableColumnKey } from "../../screens/History/HistoryUtils";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const HistoryColumnDragContext = createContext(null);

const ColumnResizeHandle = ({ onMouseDown }) => (
  <Popover
    title=""
    content={
      <p style={{ fontWeight: 500, color: "#565656", fontSize: "15px", lineHeight: "1.5rem", margin: 0 }}>
        Drag to resize column
      </p>
    }
  >
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      className="column-resize-handle"
      onMouseDown={onMouseDown}
      onClick={(event) => event.stopPropagation()}
    />
  </Popover>
);

const DraggableHeaderCell = ({
  columnKey,
  children,
  className,
  onResizeMouseDown,
  ...rest
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey });

  const style = {
    ...rest.style,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    userSelect: "none",
    ...(isDragging
      ? {
          zIndex: 100,
          background: "#fafafa",
          position: "relative",
        }
      : {}),
  };

  return (
    <th
      {...rest}
      ref={setNodeRef}
      style={style}
      className={`dnd-column-header${className ? ` ${className}` : ""}${
        isDragging ? " dnd-column-header--dragging" : ""
      }`}
    >
      <span className="dnd-column-header-content">
        <Popover
          title=""
          content={
            <p style={{ fontWeight: 500, color: "#565656", fontSize: "15px", lineHeight: "1.5rem", margin: 0 }}>
              Drag to reorder column
            </p>
          }
        >
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="dnd-column-header-grip field-item-grip-btn"
            aria-label="Drag to reorder column"
            {...attributes}
            {...listeners}
          >
            <MenuOutlined
              style={{
                touchAction: "none",
              }}
            />
          </button>
        </Popover>
        <span className="dnd-column-header-label-box">
          <span className="dnd-column-header-label">{children}</span>
        </span>
        {typeof onResizeMouseDown === "function" && (
          <ColumnResizeHandle onMouseDown={onResizeMouseDown} />
        )}
      </span>
    </th>
  );
};

const StableHeaderCell = ({ column, children, ...rest }) => {
  const ctx = useContext(HistoryColumnDragContext);
  const columnKey = rest["data-column-key"] || column?.dataIndex || column?.key;
  const onResizeMouseDown = rest.onResizeMouseDown;

  const cleanRest = { ...rest };
  delete cleanRest.onResizeMouseDown;

  if (
    !ctx ||
    !ctx.canReorder ||
    !columnKey ||
    !ctx.sortableIdSet.has(columnKey)
  ) {
    return (
      <th
        {...cleanRest}
        className={`dnd-column-header dnd-column-header--stable${
          cleanRest.className ? ` ${cleanRest.className}` : ""
        }`}
      >
        <span className="dnd-column-header-content">
          <span className="dnd-column-header-label-box" style={{ flex: 1, textAlign: "center" }}>
            <span className="dnd-column-header-label">{children}</span>
          </span>
          {typeof onResizeMouseDown === "function" && (
            <ColumnResizeHandle onMouseDown={onResizeMouseDown} />
          )}
        </span>
      </th>
    );
  }

  return (
    <DraggableHeaderCell
      columnKey={columnKey}
      onResizeMouseDown={onResizeMouseDown}
      {...cleanRest}
    >
      {children}
    </DraggableHeaderCell>
  );
};

const StableHeaderWrapper = (props) => {
  const ctx = useContext(HistoryColumnDragContext);
  if (!ctx || !ctx.canReorder) {
    return <thead {...props} />;
  }

  return (
    <DndContext
      id="history-column-dnd"
      sensors={ctx.sensors}
      collisionDetection={closestCenter}
      autoScroll={false}
      onDragStart={ctx.onDragStart}
      onDragEnd={ctx.onDragEnd}
      modifiers={[restrictToHorizontalAxis]}
    >
      <SortableContext
        items={ctx.sortableColumnIds}
        strategy={horizontalListSortingStrategy}
      >
        <thead {...props} />
      </SortableContext>
    </DndContext>
  );
};

const historyTableComponents = {
  header: {
    cell: StableHeaderCell,
    wrapper: StableHeaderWrapper,
  },
};

const HISTORY_GRID_DEFAULT_WIDTHS = {
  spec_concat: 130,
  card_no: 150,
  defect_no: 120,
  compliance_count: 150,
  raw_findings_rate: 150,
  total_effort_hours: 150,
  total_labor_cost_max: 220,
  yield_percentage: 130,
  mntc_check: 150,
  mntc_check_interval: 200,
  fec: 100,
  wpm_score: 120,
  rank: 100,
  spec_type: 120,
  mntc_check_package: 180,
  mntc_check_package_interval: 250,
  escalated_on: 150,
  comments: 120,
  assigned_to: 200,
};

const HistoryTable = ({
  listData,
  selectedColumns,
  setSelectedColumns,
  frozenColumns = [],
  sortBy,
  sortDir,
  onColumnSort,
  page,
  pageSize,
  totalRecords,
  getHistoryDataFun,
  columnWidths = {},
  setColumnWidths,
  onPreferenceAction,
}) => {
  const [dataSource, setDataSource] = useState([]);
  const [openComment, setOpenComment] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [commentSpec, setCommentSpec] = useState("");

  // Reorder / resize / sort are only offered when the screen supplies the
  // handler that persists them. Escalation Completed renders a read-only grid,
  // so it passes none and gets plain headers with no grip, handle or sort
  // affordance.
  const canReorderColumns = typeof setSelectedColumns === "function";
  const canResizeColumns = typeof setColumnWidths === "function";
  const canSortColumns = typeof onColumnSort === "function";

  const getResizeMouseDownHandler = useCallback(
    (columnKey, currentWidth) => (event) => {
      if (!columnKey) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const minWidth = 80;
      const baseWidth = currentWidth || 120;

      let finalWidth = baseWidth;

      // Returning the previous object unchanged makes React bail out, so a drag
      // that cannot move the column (already clamped at the minimum width)
      // never re-renders and never reaches the parent's save effect.
      const applyWidth = (width) =>
        setColumnWidths((prev) =>
          prev[columnKey] === width ? prev : { ...prev, [columnKey]: width },
        );

      const onMouseMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        finalWidth = Math.max(minWidth, Math.round(baseWidth + delta));

        applyWidth(finalWidth);
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);

        // A click on the handle without a drag — or a drag that ends back at
        // the starting width — is not a resize.
        if (finalWidth === baseWidth) return;

        // The width is persisted by the grid preferences save this state change
        // triggers in the parent — the API is the only store.
        onPreferenceAction?.("resize");
        applyWidth(finalWidth);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [onPreferenceAction, setColumnWidths],
  );

  const columnDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const sortableColumnIds = useMemo(
    () => selectedColumns || [],
    [selectedColumns],
  );

  const sortableIdSet = useMemo(
    () => new Set(sortableColumnIds),
    [sortableColumnIds.join(",")],
  );

  const handleDragStart = () => {};

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || !setSelectedColumns) return;

    const oldIndex = selectedColumns.indexOf(active.id);
    const newIndex = selectedColumns.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Frozen columns render as a contiguous left block, so ignore a drop that
    // crosses the frozen / unfrozen boundary.
    if (frozenColumns.includes(active.id) !== frozenColumns.includes(over.id)) {
      return;
    }

    const moved = arrayMove([...selectedColumns], oldIndex, newIndex);
    // Frozen columns always render as a contiguous left block; keep the stored
    // order matching that so state and display cannot drift apart.
    const reordered = frozenColumns.length
      ? [
          ...moved.filter((colKey) => frozenColumns.includes(colKey)),
          ...moved.filter((colKey) => !frozenColumns.includes(colKey)),
        ]
      : moved;

    // The new order is persisted by the grid preferences save this state change
    // triggers in the parent — the API is the only store.
    onPreferenceAction?.("reorder");
    setSelectedColumns(reordered);
  };

  const dragContextValue = useMemo(
    () => ({
      sensors: columnDragSensors,
      sortableColumnIds,
      sortableIdSet,
      canReorder: canReorderColumns,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
    }),
    [
      columnDragSensors,
      sortableColumnIds,
      sortableIdSet,
      canReorderColumns,
      selectedColumns,
      setSelectedColumns,
      frozenColumns,
      onPreferenceAction,
    ],
  );

  useEffect(() => {
    let arr = listData.map((ele, index) => {
      ele.key = `${index + 1}`;
      return ele;
    });
    setDataSource(arr);
  }, [listData]);

  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (extra?.action === "sort") {
      return;
    }
    const { current, pageSize } = pagination;

    getHistoryDataFun(current, pageSize);
  };

  // Derived during render like the ranking grid does. Held in state, the
  // columns lagged their inputs by a commit: on drop the headers re-rendered in
  // the old order first, so dnd-kit animated them back to their pre-drag spots,
  // and only then did the effect apply the new order -- a snap-back followed by
  // a jump. Deriving here puts the reorder in the same commit as
  // selectedColumns, so the transition runs straight to the final position.
  const columns = useMemo(() => {
    // Order comes from selectedColumns; columnsArr only supplies each definition.
    const activeCols = [];
    selectedColumns.forEach((columnKey) => {
      const ele = columnsArr.find((c) => c.dataIndex === columnKey);
      if (ele) {
        activeCols.push({ ...ele });
      }
    });

    // Sticky freeze only works as a contiguous left block, so frozen columns
    // render first. Both groups keep their relative order from selectedColumns.
    const frozenCols = [];
    const unfrozenCols = [];

    activeCols.forEach((col) => {
      const colKey = col.dataIndex || col.key;
      if (frozenColumns.includes(colKey)) {
        frozenCols.push(col);
      } else {
        unfrozenCols.push(col);
      }
    });

    const orderedCols = [...frozenCols, ...unfrozenCols];

    const renderSortableTitle = (label, columnKey) => {
      const isActiveColumn = sortBy === columnKey;
      const sortHint = isActiveColumn
        ? sortDir === "asc"
          ? "Sorted ascending — click to sort descending"
          : sortDir === "desc"
          ? "Sorted descending — click to clear sort"
          : "Click to sort"
        : "Click to sort";

      // Sorting is triggered from the label/indicator only, so dragging,
      // resizing or clicking anywhere else in the header cell never sorts.
      const triggerSort = (event) => {
        event.stopPropagation();
        onColumnSort?.(columnKey);
      };

      return (
        <Popover
          title=""
          content={
            <p
              style={{
                fontWeight: 500,
                color: "#565656",
                fontSize: "15px",
                lineHeight: "1.5rem",
                margin: 0,
              }}
            >
              {sortHint}
            </p>
          }
        >
          <span
            className="ranking-column-header-title ranking-column-header-title--sortable"
            role="button"
            tabIndex={0}
            aria-label={`${typeof label === "string" ? label : columnKey}: ${sortHint}`}
            onClick={triggerSort}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                triggerSort(event);
              }
            }}
          >
            <span className="ranking-column-header-label">{label}</span>
            {isActiveColumn && (sortDir === "asc" || sortDir === "desc") && (
              <span className="ranking-column-sort-indicator" aria-hidden="true">
                {sortDir === "asc" ? (
                  <CaretUpOutlined className="active" />
                ) : (
                  <CaretDownOutlined className="active" />
                )}
              </span>
            )}
          </span>
        </Popover>
      );
    };

    const withSorting = (column, columnKey) => {
      if (!canSortColumns || !isSortableColumnKey(columnKey)) {
        return column;
      }

      return {
        ...column,
        title: renderSortableTitle(column.title, columnKey),
      };
    };

    let cumulativeLeft = 0;
    const lastFrozenKey = frozenCols.length > 0
      ? frozenCols[frozenCols.length - 1].dataIndex || frozenCols[frozenCols.length - 1].key
      : null;

    const colsWithSticky = orderedCols.map((col, index) => {
      const colKey = col.dataIndex || col.key;
      const isFrozen = frozenColumns.includes(colKey);
      const defaultWidth = HISTORY_GRID_DEFAULT_WIDTHS[colKey] || parseInt(col.width) || 120;
      const colWidth = columnWidths[colKey] || defaultWidth;

      let processedCol = col;

      if (isFrozen) {
        const stickyLeft = cumulativeLeft;
        cumulativeLeft += colWidth;

        const isLastFrozen = colKey === lastFrozenKey;

        const originalOnHeaderCell = col.onHeaderCell;
        const originalOnCell = col.onCell;

        processedCol = {
          ...col,
          width: colWidth,
          onHeaderCell: (c) => {
            const existingHeaderProps = originalOnHeaderCell ? originalOnHeaderCell(c) : {};
            return {
              ...existingHeaderProps,
              "data-column-key": colKey,
              ...(canResizeColumns
                ? {
                    onResizeMouseDown: getResizeMouseDownHandler(
                      colKey,
                      colWidth,
                    ),
                  }
                : {}),
              className: [
                "history-frozen-cell",
                isLastFrozen ? "history-frozen-cell--last" : null,
                existingHeaderProps.className,
              ]
                .filter(Boolean)
                .join(" "),
              style: {
                ...(existingHeaderProps.style || {}),
                position: "sticky",
                left: stickyLeft,
                zIndex: 20 + index,
              },
            };
          },
          onCell: (record, rowIndex) => {
            const existingCellProps = originalOnCell ? originalOnCell(record, rowIndex) : {};
            return {
              ...existingCellProps,
              className: [
                "history-frozen-cell",
                isLastFrozen ? "history-frozen-cell--last" : null,
                existingCellProps.className,
              ]
                .filter(Boolean)
                .join(" "),
              style: {
                ...(existingCellProps.style || {}),
                position: "sticky",
                left: stickyLeft,
                zIndex: 10 + index,
              },
            };
          },
        };
      } else {
        const originalOnHeaderCell = col.onHeaderCell;
        processedCol = {
          ...col,
          width: colWidth,
          onHeaderCell: (c) => {
            const existingHeaderProps = originalOnHeaderCell ? originalOnHeaderCell(c) : {};
            return {
              ...existingHeaderProps,
              "data-column-key": colKey,
              ...(canResizeColumns
                ? {
                    onResizeMouseDown: getResizeMouseDownHandler(
                      colKey,
                      colWidth,
                    ),
                  }
                : {}),
            };
          },
        };
      }

      return withSorting(processedCol, colKey);
    });

    return colsWithSticky;
  }, [selectedColumns, frozenColumns, sortBy, sortDir, onColumnSort, columnWidths, getResizeMouseDownHandler, canResizeColumns, canSortColumns]);

  const Row = ({ children, ...props }) => {
    return (
      <tr {...props} style={{ ...props.style }}>
        {React.Children.map(children, (child) => {
          let strDate = "";
          if (child.props?.record?.assigned_on) {
            strDate = new Date(child.props?.record?.assigned_on);
          }
          if (child.key === "spec_concat") {
            return React.cloneElement(child, {
              children: (
                <div>
                  <Tooltip title={child.props.record?.task_description}>
                    <span style={{ cursor: "pointer" }}>
                      {child.props.record?.spec_concat}
                    </span>
                  </Tooltip>
                </div>
              ),
            });
          }
          if (child.key === "comments") {
            return React.cloneElement(child, {
              children: child.props.record.comments ? (
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: "#EBEEF8",
                    },
                  }}
                >
                  <Button
                    onClick={() => {
                      setOpenComment(true);
                      setSelectedComments(child.props.record.comments);
                      setCommentSpec(child.props.record?.spec_concat);
                    }}
                    type="primary"
                    style={{ color: "#304CB2" }}
                    icon={<EyeFilled />}
                  >
                    View
                  </Button>
                </ConfigProvider>
              ) : (
                <span>-</span>
              ),
            });
          }
          if (child.key === "assigned_to") {
            return React.cloneElement(child, {
              children: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: child.props.record.assigned_to
                      ? "space-between"
                      : "center",
                  }}
                >
                  {child.props.record.assigned_to ? (
                    `${child.props.record.assigned_to} - on  ${String(strDate).slice(4, 15)}`
                  ) : (
                    <span>-</span>
                  )}
                </div>
              ),
            });
          }
          return child;
        })}
      </tr>
    );
  };

  return (
    <div>
      <HistoryColumnDragContext.Provider value={dragContextValue}>
        <Table
          scroll={{ x: "max-content" }}
          components={{
            body: {
              row: Row,
            },
            header: historyTableComponents.header,
          }}
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          className={`antdTable history-table${frozenColumns.length > 0 ? " history-table--has-frozen" : ""}`}
          locale={{
            emptyText: <TableEmptyContent />,
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      </HistoryColumnDragContext.Provider>
      <ViewHistoryComments
        open={openComment}
        setOpen={setOpenComment}
        messageData={selectedComments}
        commentSpec={commentSpec}
      />
    </div>
  );
};
export default HistoryTable;
